<script setup>
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { api } from '@/lib/http.js'
import { reportError } from '@/composables/useSave.js'
import { offerLanguages } from '@/lib/rowTexts.js'
import { formatDate, formatRelative } from '@/lib/format.js'
import { useMeta } from '@/stores/meta.js'
import RecordLink from '@/components/record/RecordLink.vue'
import AttachmentsPanel from '@/components/record/AttachmentsPanel.vue'
import RelationCard from '@/components/catalogue/RelationCard.vue'
import UiButton from '@/components/ui/UiButton.vue'

/**
 * A sales material's own cards under its form (D-332): its files, each for a language or all; its
 * links; what it is about (products, modules, components); whom it fits — competitors, industries,
 * stages of a sale; how much it is used. Each change saved at once.
 */
const props = defineProps({ record: { type: Object, required: true }, editable: Boolean })
const emit = defineEmits(['changed'])
const { t } = useI18n()
const meta = useMeta()
const L = (v) => meta.localized(v)
const languages = computed(() => offerLanguages(meta))
const busy = ref(false)

async function save(body) {
  busy.value = true
  try {
    await api(`/sales-materials/${props.record.uuid}`, { method: 'PATCH', body })
    emit('changed')
  } catch (e) {
    reportError(e)
  } finally {
    busy.value = false
  }
}

// What may be named: the catalogue's elements, the competitors, the industries, the stages of opportunities.
const elements = ref([])
const competitors = ref([])
async function loadOptions() {
  if (!props.editable) return
  try {
    const [prods, comps, rivals] = await Promise.all([api('/catalog/products').catch(() => ({ data: [] })), api('/catalog/components').catch(() => ({ data: [] })),
      api('/competitors?per_page=500&sort=name').catch(() => ({ data: [] }))])
    elements.value = [
      ...prods.data.map((p) => ({ value: `product:${p.uuid}`, label: L(p.name), group: t('salesMaterial.types.product') })),
      ...prods.data.flatMap((p) => (p.modules || []).map((m) => ({ value: `module:${m.uuid}`, label: L(m.name), hint: L(p.name), group: t('salesMaterial.types.module') }))),
      ...comps.data.map((c) => ({ value: `component:${c.uuid}`, label: L(c.name), hint: c.code || '', group: t('salesMaterial.types.component') })),
    ]
    competitors.value = rivals.data.map((c) => ({ value: c.uuid, label: c.label || c.name }))
  } catch (e) {
    reportError(e)
  }
}
watch(() => props.editable, loadOptions, { immediate: true })
const industries = computed(() => meta.dictionary('industry').entries.map((e) => ({ value: e.key, label: L(e.label) })))
const stages = computed(() => meta.pipelines('opportunity').flatMap((p) => p.stages.map((s) => ({ value: s.uuid, label: L(s.name), hint: meta.pipelines('opportunity').length > 1 ? L(p.name) : '' }))))

const ROUTES = { product: 'catalogue-product', module: 'catalogue-module', component: 'catalogue-component' }
const aboutItems = computed(() => (props.record.about_items || []).map((e) => ({ key: `${e.type}:${e.uuid}`, label: e.label, to: { name: ROUTES[e.type], params: { uuid: e.uuid } },
  hint: [t(`salesMaterial.types.${e.type}`), e.context].filter(Boolean).join(' · ') })))
const about = computed(() => (props.record.about || []).map((e) => `${e.type}:${e.uuid}`))
const toAbout = (keys) => keys.map((k) => ({ type: k.split(':')[0], uuid: k.split(':')[1] }))
const competitorItems = computed(() => (props.record.competitor_items || []).map((c) => ({ key: c.uuid, label: c.label, to: { name: 'record', params: { entityPath: 'competitors', uuid: c.uuid } } })))
const industryItems = computed(() => (props.record.industries || []).map((k) => ({ key: k, label: meta.dictionaryLabel('industry', k) })))
const stageItems = computed(() => (props.record.stage_items || []).map((s) => ({ key: s.uuid, label: L(s.label) || s.label, hint: s.context || '' })))
const listOf = (field) => props.record[field] || []

// Links: edited here, saved with "Save".
const links = ref([])
watch(() => props.record.links, (v) => (links.value = (v || []).map((l) => ({ url: l.url, label: l.label || '', language: l.language || '' }))), { immediate: true })
const linksDirty = computed(() => JSON.stringify(links.value) !== JSON.stringify((props.record.links || []).map((l) => ({ url: l.url, label: l.label || '', language: l.language || '' }))))
const saveLinks = () => save({ links: links.value.filter((l) => l.url.trim()).map((l) => ({ url: l.url.trim(), label: l.label || null, language: l.language || null })) })
const usage = computed(() => props.record.usage || { sent: 0, recent: [] })
</script>

<template>
  <div class="space-y-4" data-testid="sales-material-panel">
    <section class="card space-y-2 p-3">
      <h3 class="flex items-center gap-1.5 text-sm font-semibold">{{ $t('salesMaterial.files') }} <i class="fa-solid fa-circle-info cursor-help text-xs text-faint" :title="$t('salesMaterial.filesHint')" aria-hidden="true"></i></h3>
      <AttachmentsPanel entity="sales_material" :record="record" :languages="languages" />
    </section>

    <section class="card space-y-2 p-3" data-testid="material-links">
      <h3 class="flex items-center gap-1.5 text-sm font-semibold">{{ $t('salesMaterial.links') }} <i class="fa-solid fa-circle-info cursor-help text-xs text-faint" :title="$t('salesMaterial.linksHint')" aria-hidden="true"></i>
        <span class="flex-1"></span>
        <UiButton v-if="editable" size="xs" variant="ghost" icon="fa-plus" data-testid="material-link-add" @click="links.push({ url: '', label: '', language: '' })">{{ $t('salesMaterial.addLink') }}</UiButton>
      </h3>
      <p v-if="!links.length" class="text-sm text-faint">—</p>
      <div v-for="(l, i) in links" :key="i" class="flex flex-wrap items-center gap-2">
        <template v-if="editable">
          <input v-model="l.url" type="url" class="input min-w-[14rem] flex-1 py-1 text-sm" placeholder="https://" :aria-label="$t('salesMaterial.links')" data-testid="material-link-url" />
          <input v-model="l.label" class="input w-48 py-1 text-sm" :placeholder="$t('salesMaterial.linkLabel')" :aria-label="$t('salesMaterial.linkLabel')" />
          <select v-if="languages.length > 1" v-model="l.language" class="input w-auto py-1 text-sm" :aria-label="$t('attachments.fileLanguage')"><option value="">{{ $t('attachments.allLanguages') }}</option><option v-for="x in languages" :key="x" :value="x">{{ x.toUpperCase() }}</option></select>
          <button type="button" class="p-1 text-faint hover:text-red-600" :aria-label="$t('common.delete')" @click="links.splice(i, 1)"><i class="fa-solid fa-xmark" aria-hidden="true"></i></button>
        </template>
        <template v-else>
          <i class="fa-solid fa-arrow-up-right-from-square text-blue-600" aria-hidden="true"></i>
          <a :href="l.url" target="_blank" rel="noopener noreferrer" class="link break-all text-sm">{{ l.label || l.url }}</a>
          <span v-if="l.language" class="text-xs uppercase text-muted">{{ l.language }}</span>
        </template>
      </div>
      <div v-if="editable && linksDirty" class="flex justify-end gap-2">
        <UiButton size="sm" @click="links = (record.links || []).map((x) => ({ url: x.url, label: x.label || '', language: x.language || '' }))">{{ $t('common.cancel') }}</UiButton>
        <UiButton size="sm" variant="primary" icon="fa-floppy-disk" :loading="busy" data-testid="material-links-save" @click="saveLinks">{{ $t('common.save') }}</UiButton>
      </div>
    </section>

    <RelationCard :title="$t('salesMaterial.about')" icon="fa-cubes" :tip="$t('salesMaterial.aboutHint')" :items="aboutItems" :options="elements" :taken="about" :editable="editable"
                  :placeholder="$t('salesMaterial.aboutSearch')" data-testid="material-about"
                  @add="(v) => save({ about: toAbout([...about, v]) })" @remove="(k) => save({ about: toAbout(about.filter((x) => x !== k)) })" />
    <div class="grid gap-4 lg:grid-cols-3">
      <RelationCard :title="$t('salesMaterial.competitors')" icon="fa-chess-knight" :tip="$t('salesMaterial.competitorsHint')" :items="competitorItems" :options="competitors" :taken="listOf('competitors')" :editable="editable"
                    data-testid="material-competitors" @add="(v) => save({ competitors: [...listOf('competitors'), v] })" @remove="(k) => save({ competitors: listOf('competitors').filter((x) => x !== k) })" />
      <RelationCard :title="$t('salesMaterial.industries')" icon="fa-industry" :tip="$t('salesMaterial.industriesHint')" :items="industryItems" :options="industries" :taken="listOf('industries')" :editable="editable"
                    data-testid="material-industries" @add="(v) => save({ industries: [...listOf('industries'), v] })" @remove="(k) => save({ industries: listOf('industries').filter((x) => x !== k) })" />
      <RelationCard :title="$t('salesMaterial.stages')" icon="fa-stairs" :tip="$t('salesMaterial.stagesHint')" :items="stageItems" :options="stages" :taken="listOf('stages')" :editable="editable"
                    data-testid="material-stages" @add="(v) => save({ stages: [...listOf('stages'), v] })" @remove="(k) => save({ stages: listOf('stages').filter((x) => x !== k) })" />
    </div>

    <section class="card space-y-2 p-3 text-sm" data-testid="material-usage">
      <h3 class="text-sm font-semibold">{{ $t('salesMaterial.usage') }}</h3>
      <p v-if="!usage.sent" class="text-faint">{{ $t('salesMaterial.usageNone') }}</p>
      <template v-else>
        <p class="text-muted">{{ $t('salesMaterial.usageSent', { n: usage.sent }) }}<template v-if="usage.last_sent_at"> · {{ $t('salesMaterial.usageLast', { when: formatRelative(usage.last_sent_at) }) }}</template></p>
        <ul class="divide-y divide-[var(--border)]">
          <li v-for="(s, i) in usage.recent" :key="i" class="flex flex-wrap items-center gap-2 py-1">
            <span class="tabular-nums text-xs text-faint">{{ formatDate(s.sent_at) }}</span>
            <RecordLink v-if="s.regarding" class="link min-w-0 flex-1 truncate" :entity="s.regarding.entity" :uuid="s.regarding.uuid">{{ s.regarding.label }}</RecordLink>
            <span v-else class="flex-1 text-faint">—</span>
            <span v-if="s.by" class="text-xs text-muted">{{ s.by }}</span>
          </li>
        </ul>
      </template>
    </section>
  </div>
</template>
